/**
 * Capabilities — derived from the role in ONE place (§7). Product code asks `can('assignment.write')`,
 * never compares role names. Every capability here has a matching data-tier rule (0003/0004 policies and
 * triggers); the rank ladder hotel_admin (3) > event_host (2) > checkin_staff (1) is declared once, in SQL
 * (`role_rank`) and mirrored here for rendering only — the database is the control. `event_only` (rank 0) holds no
 * organization capability at all: what it can do comes from its own per-event grants (`eventOnlyCapabilities`).
 */
export type Role = 'hotel_admin' | 'event_host' | 'checkin_staff' | 'event_only';

export type Capability =
  | 'org.read' | 'event.read' | 'guest.read' | 'room.read' | 'board.read'
  | 'audit.read' | 'report.read' | 'settings.read'
  | 'org.manage' | 'settings.manage' | 'member.manage'
  | 'hotel.manage' | 'hotel.delete' | 'floorplan.manage'
  | 'event.create' | 'event.manage' | 'event.delete'
  | 'guest.manage' | 'guest.delete' | 'assignment.write' | 'checkin.write'
  | 'message.send' | 'payment.manage' | 'export.run'
  | 'call.use' | 'call.manage'
  | 'property.read'
  | 'hotel.portal'
  | 'platform.manage';

const CHECKIN: Capability[] = ['event.read', 'guest.read', 'room.read', 'board.read', 'checkin.write', 'call.use', 'property.read'];
const HOST: Capability[] = [...CHECKIN, 'event.create', 'event.manage', 'guest.manage', 'assignment.write', 'message.send',
  'export.run', 'hotel.manage', 'floorplan.manage', 'audit.read', 'settings.read', 'call.manage'];
const ADMIN: Capability[] = [...HOST, 'org.read', 'org.manage', 'settings.manage', 'member.manage', 'event.delete', 'guest.delete', 'hotel.delete', 'report.read', 'payment.manage'];

export const CAPABILITIES: Record<Role, ReadonlySet<Capability>> = {
  checkin_staff: new Set(CHECKIN),
  event_host: new Set(HOST),
  hotel_admin: new Set(ADMIN),
  // nothing organization-wide: an event-only person's capabilities come from their event grants alone
  event_only: new Set(),
};
export const HOTEL_ACCOUNT_CAPABILITIES: ReadonlySet<Capability> = new Set(['hotel.portal']);
export const NONE: ReadonlySet<Capability> = new Set();

export const RANK: Record<Role, number> = { hotel_admin: 3, event_host: 2, checkin_staff: 1, event_only: 0 };

/** Unknown, missing or malformed role → no capabilities at all. No fallback ever escalates (§7). */
export function capabilitiesFor(role: unknown): ReadonlySet<Capability> {
  return typeof role === 'string' && role in CAPABILITIES ? CAPABILITIES[role as Role] : NONE;
}

/** What any event grant gives an event-only person: read the event, its rooms and guests, check in, work its phone line. */
const EVENT_BASE: Capability[] = ['event.read', 'guest.read', 'room.read', 'board.read', 'checkin.write', 'call.use'];
const EVENT_HOST_GRANT: Capability[] = ['event.manage', 'guest.manage', 'assignment.write', 'message.send', 'export.run', 'audit.read', 'call.manage'];
const EVENT_ADMIN_GRANT: Capability[] = [...EVENT_HOST_GRANT, 'guest.delete', 'payment.manage', 'report.read'];

/**
 * An event-only person runs the events they are granted, and sees nothing organization-wide: never event.create,
 * properties, floor plans, settings, the organization, members, event deletion or the platform. With grants of
 * different roles on several events the menu shows the union; the database still refuses per event (logged limit).
 * No grant, or only unknown roles → no capabilities.
 */
export function eventOnlyCapabilities(grants: readonly Role[]): ReadonlySet<Capability> {
  const known = grants.filter((g) => g === 'hotel_admin' || g === 'event_host' || g === 'checkin_staff');
  if (!known.length) return NONE;
  const out = new Set<Capability>(EVENT_BASE);
  if (known.includes('event_host')) for (const c of EVENT_HOST_GRANT) out.add(c);
  if (known.includes('hotel_admin')) for (const c of EVENT_ADMIN_GRANT) out.add(c);
  return out;
}

export const ROLE_LABEL = {
  hotel_admin: 'Organization admin',
  event_host: 'Event host',
  checkin_staff: 'Check-in staff',
} as Record<Role, string>;
// Labelled but not listed: the organization's role pickers offer Object.keys(ROLE_LABEL), and event-only access is
// given from an event's Team card, never from those pickers.
Object.defineProperty(ROLE_LABEL, 'event_only', { value: 'Event access', enumerable: false });

/** Why a disabled affordance is disabled — stated, never a silent grey button (§7). */
/** Why someone can't do something — with a page name, it says which page first (walk 4: "can't change event settings" on Payments read oddly). */
export function deniedReason(cap: Capability, role: Role | null, page?: string): string {
  const who = role === 'event_only' ? 'Your event access' : role ? ROLE_LABEL[role] : 'Your account';
  const what: Partial<Record<Capability, string>> = {
    'assignment.write': 'change room assignments',
    'guest.manage': 'add or edit guests',
    'guest.delete': 'erase or delete guests',
    'event.manage': 'change event settings',
    'event.create': 'create events',
    'event.delete': 'delete events',
    'floorplan.manage': 'edit floor plans',
    'hotel.manage': 'edit properties',
    'hotel.delete': 'delete properties',
    'message.send': 'send messages',
    'payment.manage': 'record or refund payments',
    'settings.manage': 'change organization settings',
    'member.manage': 'manage members',
    'export.run': 'export data',
    'checkin.write': 'check guests in',
    'call.use': 'work on the event’s phone line',
    'call.manage': 'change how the event’s phone line rings',
  };
  if (page) return `${who} can't open ${page} — it's for people who ${what[cap] ?? 'have more access'}. Ask an organization admin.`;
  return `${who} can't ${what[cap] ?? 'do this'}. Ask an organization admin.`;
}
